import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loading } from '@shape-ds/components';
import { InfoMessage } from '../InfoMessage/InfoMessage';
import EmptyMessage from '../EmptyMessage/EmptyMessage';
import { SensorsGraphic } from '../SensorsGraphic';
import { useChartVariables } from './hooks/Chart.context';
import './style.scss';
import { abreviateUnits, buildSeries, formatChartData } from './helpers/helper';
import { mountUnitAxis } from './helpers/elementsParse';

const LineChart: React.FC<LineChartsProps> = ({
  chartData,
  alarmsInfo,
  isFetching,
  isError,
  overrideGraphicOptions,
  tagsConfig,
  customThrehold,
  isFlaring,
  overrideUnits,
}) => {
  const { t } = useTranslation();
  const { excludeGap, setChartLegends, selectedChartVariables, dateRange, chartId, printRef } =
    useChartVariables();

  const { series, yAxis } = useMemo(() => {
    if (chartData && chartData.length > 0) {
      const { sensors, thresholds, units } = formatChartData(chartData, tagsConfig);

      const yAxis = (overrideUnits ?? units).map((unit, index) =>
        mountUnitAxis({
          ...unit,
          offset: index * (60 - (index - 1)),
          name: abreviateUnits(unit.name),
        }),
      ) as echarts.EChartsOption['yAxis'];

      const series = buildSeries({
        sensors,
        thresholds,
        units: overrideUnits ?? units,
        alarmsInfo,
        connectNulls: excludeGap,
        dateRange,
        customThrehold,
        chartId,
      });
      return { series, yAxis };
    }
    return { series: [], yAxis: [] };
  }, [chartData, alarmsInfo]);

  useEffect(() => {
    setChartLegends(
      Array.from(tagsConfig.values()).map(
        ({ label, color, category, description, tooltipText, reference }) => ({
          label,
          color,
          category,
          description,
          tooltipText,
          reference,
        }),
      ),
    );
  }, [tagsConfig]);

  if (isFetching)
    return (
      <div className="line-chart loading" data-testid="loading">
        <Loading />
      </div>
    );
  if (isError) {
    return (
      <div className="line-chart error" data-testid="line-chart-error">
        <InfoMessage
          type="error"
          message={t('components:line_chart.error')}
          iconSize={24}
          columnSize={12}
        />
      </div>
    );
  }
  if (
    !chartData ||
    chartData.length === 0 ||
    selectedChartVariables.length === 0 ||
    !tagsConfig ||
    tagsConfig.size === 0
  ) {
    return (
      <EmptyMessage
        message={
          selectedChartVariables.length === 0
            ? t('components:line_chart.no_data')
            : t('components:line_chart.no_data_for', { name: selectedChartVariables.join(', ') })
        }
        title={t('components:line_chart.try_other')}
        className="line-chart error"
        data-testid="line-chart-empty"
      />
    );
  }
  return (
    <div className="line-chart" data-testid="line-chart">
      <SensorsGraphic
        isFlaring={isFlaring}
        graphicOptions={{
          yAxis,
          series,
          ...overrideGraphicOptions,
        }}
        printRef={printRef}
      />
    </div>
  );
};

export default LineChart;
